import type { Params } from 'react-router-dom'
import { matchPath, useParams } from 'react-router-dom'
import moize from 'moize'
import { mapValues } from 'radash'

import type {
  FileRouteParams,
  FileRouteSortBy,
  FileRouteViewMode,
} from '@jotta/types/schemas/FileRouteSchema'
import { makeFileRouteCatchSchema } from '@jotta/types/schemas/FileRouteSchema'
import { safeDecodeURIComponent, uriDecodePath } from '@jotta/utils/pathUtils'
import { isString } from '@jotta/utils/typeAssertions'

import { sortByCookie, viewModeCookie } from './cookies'

function decodeParams<T extends string>(
  params: Params<T> | undefined,
): typeof params {
  if (params) {
    return mapValues(params, (v, k) =>
      v ? (k === '*' ? uriDecodePath(v) : safeDecodeURIComponent(v)) : v,
    )
  }
  return undefined
}
function fileRoutePathToParams(path: string) {
  const searchMatch = matchPath(
    '/web/search/:viewMode/:sortBy/:query/*',
    path,
  )?.params
  if (searchMatch) {
    return {
      context: 'search',
      ...decodeParams(searchMatch),
    }
  }
  return (
    decodeParams(
      matchPath('/web/:context/:viewMode/:sortBy/*', path)?.params,
    ) ||
    decodeParams(matchPath('/web/:context/:viewMode', path)?.params) ||
    decodeParams(matchPath('/web/:context', path)?.params)
  )
}

/**
 * Parse value to valid FileRouteParams.
 *
 * Cookie prefs are part of the memo key because incomplete routes such as
 * `/web/archive` fill sort/viewMode from cookies.
 */
export function getFileRouteParams(value: unknown): FileRouteParams {
  return getCachedFileRouteParams(
    value,
    viewModeCookie.get(),
    sortByCookie.get(),
  )
}

const getCachedFileRouteParams = moize.shallow(
  function getCachedFileRouteParams(
    value: unknown,
    viewMode: FileRouteViewMode,
    sortBy: FileRouteSortBy,
  ): FileRouteParams {
    const params = isString(value) ? fileRoutePathToParams(value) : value

    return makeFileRouteCatchSchema({
      viewMode,
      sortBy,
    }).parse(params)
  },
)

export function useFileRouteParams() {
  const params = useParams()

  return getFileRouteParams(params)
}
